import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { TrendencyReqService } from '../../../../trendency/http';
import { DateFormatPipe } from '../pipes/date-format.pipe';
import { IAccommodationNatureFilters, IContentListParams, IEquipmentFilters, IFilterOption, IPriceRange } from '../shared.definitions';
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private readonly reqService: TrendencyReqService, private readonly translate: TranslateService) {}

  get lang(): string {
    return this.translate.currentLang;
  }

  getAllLangTranslations$(): Observable<any> {
    return this.reqService.get(`/${this.lang}/list-translations`);
  }

  getTranslations$(): Observable<any> {
    return this.reqService.get(`/${this.lang}/translations`);
  }

  getInit$(): Observable<any> {
    return this.reqService.get(`init`);
  }

  getContentList$(listType: string): Observable<any> {
    return this.reqService.get(`/${this.lang}/content/${listType}`);
  }

  getContentListByParams$(listType: string, params?: IContentListParams): Observable<any> {
    let url = `/${this.lang}/content/${listType}`;

    if (!params) {
      return this.reqService.get(url);
    }

    if (params?.rowTo_limit !== undefined && params.rowTo_limit >= 0) {
      url += `?rowFrom_limit=${params.rowFrom_limit}`;
    }
    if (params?.rowTo_limit) {
      url += `&rowTo_limit=${params.rowTo_limit}`;
    }
    if (params?.collection) {
      url += `&collection=${params.collection}`;
    }
    if (params?.global_filter) {
      url += `&global_filter=${params.global_filter}`;
    }
    if (params?.storyIdeasTags_filter) {
      params.storyIdeasTags_filter.forEach((id) => {
        url += `&storyIdeasTags_filter[]=${id}`;
      });
    }

    return this.reqService.get(url);
  }

  getContentPreview$(previewHash: string): Observable<any> {
    return this.reqService.get(`/${this.lang}/content-preview/${previewHash}`);
  }

  getContent$(pageType: string, slug: string): Observable<any> {
    return this.reqService.get(`/${this.lang}/content/${pageType}/${slug}/view`)
  }

  getDocumentsByGroup$(groupUuid: string, limit: number): Observable<any> {
    return this.reqService.get(`/${this.lang}/documents-by-group/${groupUuid}?offset_limit=${limit}`);
  }

  getDocumentsByDestination$(destinationTagUuid: string, limit: number): Observable<any> {
    return this.reqService.get(`/${this.lang}/documents-by-destination/${destinationTagUuid}?offset_limit=${limit}`);
  }

  getDocumentGroups$(): Observable<any> {
    return this.reqService.get(`/${this.lang}/document-groups`);
  }

  getMenuTreeIndex$(): Observable<any> {
    return this.reqService.get(`/${this.lang}/menu/tree`);
  }
  getFormInfos$(id: string, queryParams: { expires?: string; signature?: string; previewHash?: string }): Observable<any> {
    let url = `/hu/forms/${id}`;

    if (queryParams) {
      const params = new URLSearchParams();

      if (queryParams.expires) {
        params.set('expires', queryParams.expires.trim());
      }

      if (queryParams.signature) {
        params.set('signature', queryParams.signature.trim());
      }

      if (queryParams.previewHash) {
        params.set('previewHash', queryParams.previewHash.trim());
      }

      url += '?' + params.toString();
    }

    return this.reqService.get(url);
  }
  getFormContent$(slug: string): Observable<any> {
    return this.reqService.get(`/${this.lang}/content/Form/${slug}/view`);
  }
  postForm$(
    id: string,
    fields: any,
    queryParams: { expires?: string; signature?: string; submitSignature?: string; previewHash?: string }
  ): Observable<any> {
    let url = `/hu/forms/${id}/submit`;

    if (queryParams) {
      const params = new URLSearchParams();

      if (queryParams.expires) {
        params.set('expires', queryParams.expires);
      }

      if (queryParams.submitSignature) {
        params.set('signature', queryParams.submitSignature);
      }

      url += '?' + params.toString();
    }

    return this.reqService.post(url, { fields });
  }

  postAccommodationForm$(fields: any): Observable<any> {
    return this.reqService.post(`/hu/accommodation/fb23499a-b962-435e-b014-8ae01cc05bdd/applications`, fields );
  }

  getMenuTreeList$(groupKey: string): Observable<any> {
    return this.reqService.get(`/${this.lang}/menu/group/${groupKey}/recommendations`);
  }

  getTrips$(
    from: number,
    to: number,
    collectionUUID?: string,
    term?: string,
    destinations?: IFilterOption[],
    tripLengths?: IFilterOption[],
    targetAudiences?: IFilterOption[]
  ): Observable<any> {
    let url = `/${this.lang}/content/Trips?rowFrom_limit=${from}&rowTo_limit=${to}`;
    if (collectionUUID) {
      url += `&collection=${collectionUUID}`;
    }
    if (term) {
      url += `&global_filter=${term}`;
    }
    if (destinations) {
      destinations
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&destinationTags_filter[]=${f.id}`;
        });
    }
    if (tripLengths) {
      tripLengths
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&tripLength_filter[]=${f.id}`;
        });
    }
    if (targetAudiences) {
      targetAudiences
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&targetAudienceTags_filter[]=${f.id}`;
        });
    }
    return this.reqService.get(url);
  }

  getArticles$(
    from: number,
    to: number,
    collectionUUID?: string,
    term?: string,
    destinations?: IFilterOption[],
    thematics?: IFilterOption[]
  ): Observable<any> {
    let url = `/${this.lang}/content/Articles?rowFrom_limit=${from}&rowTo_limit=${to}`;
    if (collectionUUID) {
      url += `&collection=${collectionUUID}`;
    }
    if (term) {
      url += `&global_filter=${term}`;
    }
    if (destinations) {
      destinations
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&destinationTags_filter[]=${f.id}`;
        });
    }
    if (thematics) {
      thematics
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&thematicTags_filter[]=${f.id}`;
        });
    }
    return this.reqService.get(url);
  }

  getEvents$(
    from: number,
    to: number,
    collectionUUID?: string,
    term?: string,
    destinations?: IFilterOption[],
    thematics?: IFilterOption[],
    dateRange?: Date[],
    isArchive?: boolean
  ): Observable<any> {
    let url = `/${this.lang}/content/Events?rowFrom_limit=${from}&rowTo_limit=${to}`;
    if (collectionUUID) {
      url += `&collection=${collectionUUID}`;
    }
    if (term) {
      url += `&global_filter=${term}`;
    }
    if (destinations) {
      destinations
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&destinationTags_filter[]=${f.id}`;
        });
    }
    if (thematics) {
      thematics
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&thematicTags_filter[]=${f.id}`;
        });
    }
    if (dateRange && dateRange.length > 0) {
      const gt = new DateFormatPipe().transform(dateRange[0], 'YYYY-MM-DD');
      const lt = new DateFormatPipe().transform(dateRange[1], 'YYYY-MM-DD');
      url += `&eventDateGt_filter=${gt}&eventDateLt_filter=${lt}`;
    }
    if (isArchive) {
      url += `&isArchive=1`;
    }

    return this.reqService.get(url);
  }
  public getAccommodationDetail$(slug: string): Observable<any> {
    return this.reqService.get(`/${this.lang}/accommodation/fb23499a-b962-435e-b014-8ae01cc05bdd/applications/${slug}`);
  }

  getAttractionSearch$(slug: string): Observable<any> {
    return this.reqService.get(`/hu/attraction-search/${slug}`);
  }

  getAttractionSearchFilter$(): Observable<any> {
    return this.reqService.get(`/${this.lang}/search-filter/`);
  }

  getSearch$(slug: string, page?: number): Observable<any> {
    let url = `/hu/search/${slug}`;
    if (page) {
      url += `?page=${page}`;
    }
    return this.reqService.get(url);
  }

  getGinopRegForm$(type: string, uuid: string): Observable<any> {
    return this.reqService.get(`/en/${type}/registration?pageUuid=${uuid}`);
  }

  postGinopRegistration$(type: string, uuid: string, data: object): Observable<any> {
    return this.reqService.post(`/en/${type}/registration?pageUuid=${uuid}`, data);
  }

  getImageByUuid$(uuid: string): Observable<any> {
    return this.reqService.get(`/en/media/image-variant/${uuid}/details`);
  }

  getGinopDocumentByUuid$(uuid: string): Observable<any> {
    return this.reqService.get(`/en/documents/preview/${uuid}`);
  }

  getFile$(uuid: string): Observable<any> {
    return this.reqService.get(`/${this.lang}/documents/preview/${uuid}`);
  }

  getMiceCocktailReg$(): Observable<any> {
    return this.reqService.get(`/en/cocktail/registration`);
  }

  postMiceCocktailReg$(data: object): Observable<any> {
    return this.reqService.post(`/en/cocktail/registration`, data);
  }

  public getApplicationsCollection$(
    type: string,
    uuid: string,
    componentUuid: string,
    from: number,
    to: number,
    term?: string,
    destinations?: IFilterOption[],
    serviceType?: IFilterOption[],
    summerServices?: IFilterOption[],
    acceptsCardType?: IFilterOption[],
    accommodationType?: IFilterOption[],
    accommodationClass?: IFilterOption[],
    accommodationNature?: IAccommodationNatureFilters,
    paymentMethod?: IFilterOption[],
    equipment?: IEquipmentFilters,
    suburb?: IFilterOption[],
    city?: IFilterOption[],
    accommodationFeature?: IFilterOption[],
    accommodationService?: IFilterOption[],
    campaignOffers?: IFilterOption[],
    discountPercent?: IPriceRange,
    priceRange?: IPriceRange,
    seed?: number
  ): Observable<any> {
    const extraLang = type === 'summer-accommodation' ? '/hu' : '';
    let url = `/${this.lang}/${type}/${uuid}/applications${extraLang}?listingInfo`;
    url += `&rowFrom_limit=${from}&rowTo_limit=${to}&campaignComponentId=${componentUuid}`;
    if (seed) {
      url += `&seed=${seed}`;
    }
    if (term) {
      url += `&global_filter=${term}`;
    }
    if (destinations) {
      destinations
        .filter((f) => f.checked)
        .forEach((f) => {
          let id = 'null';
          if (f.id !== 0) {
            id = String(f.id);
          }
          url += `&destinationTag_filter[]=${id}`;
        });
    }
    if (serviceType) {
      serviceType
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&serviceTypes_filter[]=${f.id}`;
        });
    }
    if (summerServices) {
      summerServices
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&${f.id}=true`;
        });
    }
    if (acceptsCardType) {
      acceptsCardType
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&acceptsCardType[]=${f.id}`;
        });
    }
    if (accommodationType) {
      accommodationType
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&accommodationType[]=${f.id}`;
        });
    }
    if (accommodationClass) {
      accommodationClass
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&accommodationClass[]=${f.id}`;
        });
    }
    if (accommodationNature) {
      Object.keys(accommodationNature).forEach((key) => {
        if (accommodationNature[key]) {
          url += `&${key}=true`;
        }
      });
    }
    if (paymentMethod) {
      paymentMethod
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&paymentMethod[]=${f.id}`;
        });
    }
    if (equipment) {
      Object.keys(equipment).forEach((key) => {
        if (equipment[key]) {
          url += `&${key}=true`;
        }
      });
    }
    if (suburb) {
      suburb
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&suburb[]=${f.id}`;
        });
    }
    if (city) {
      city
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&city[]=${f.id}`;
        });
    }
    if (accommodationFeature) {
      accommodationFeature
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&accommodationFeature[]=${f.id}`;
        });
    }
    if (accommodationService) {
      accommodationService
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&accommodationService[]=${f.id}`;
        });
    }
    if (campaignOffers) {
      campaignOffers
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&campaignOffers[]=${f.id}`;
        });
    }
    if (discountPercent) {
      const start = discountPercent.start !== null ? discountPercent.start : '';
      const end = discountPercent.end !== null ? discountPercent.end : '';
      if (start !== 0) {
        url += `&discountPercentGt=${start}`;
      }
      if (end !== 100) {
        url += `&discountPercentLt=${end}`;
      }
    }
    if (priceRange) {
      const start = priceRange.start !== null ? priceRange.start : '';
      const end = priceRange.end !== null ? priceRange.end : '';
      if (start !== 0) {
        url += `&price_gt=${start}`;
      }
      if (end !== 1000000) {
        url += `&price_lt=${end}`;
      }
    }
    return this.reqService.get(url);
  }

  public getApplications$(
    uuid: string,
    from: number,
    to: number,
    term?: string,
    destinations?: IFilterOption[],
    summerServices?: IFilterOption[]
  ): Observable<any> {
    let url = `/${this.lang}/summer-accommodation/${uuid}/applications/en?listingInfo&rowFrom_limit=${from}&rowTo_limit=${to}`;
    if (term) {
      url += `&global_filter=${term}`;
    }
    if (destinations) {
      destinations
        .filter((f) => f.checked)
        .forEach((f) => {
          let id = 'null';
          if (f.id !== 0) {
            id = String(f.id);
          }
          url += `&destinationTag_filter[]=${id}`;
        });
    }
    if (summerServices) {
      summerServices
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&${f.id}=true`;
        });
    }
    return this.reqService.get(url);
  }

  fileUpload$(formData: FormData): Observable<any> {
    return this.reqService.post(`/${this.lang}/kpp/document-upload`, formData);
  }

  accommodationImageUpload$(formData: FormData): Observable<any> {
    return this.reqService.post(`/${this.lang}/accommodation/image_upload`, formData);
  }

  getFormList$(): Observable<any> {
    return this.reqService.get(`/${this.lang}/kpp/forms`);
  }

  public getTourinform$(uuid: string): Observable<any> {
    return this.reqService.get(`/${this.lang}/tourinform/${uuid}?listingInfo`);
  }

  public getTourinforms$(
    from?: number,
    to?: number,
    term?: string,
    destinations?: IFilterOption[],
    services?: IFilterOption[],
    languages?: IFilterOption[]
  ): Observable<any> {
    let url = `/${this.lang}/tourinforms?listingInfo`;
    if (from != null) {
      url += `&rowFrom_limit=${from}`;
    }
    if (to != null) {
      url += `&rowTo_limit=${to}`;
    }
    if (term) {
      url += `&global_filter=${term}`;
    }
    if (destinations) {
      destinations
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&destinationTags_filter[]=${f.id}`;
        });
    }
    if (services) {
      services
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&service_filter[]=${f.id}`;
        });
    }
    if (languages) {
      languages
        .filter((f) => f.checked)
        .forEach((f) => {
          url += `&language_filter[]=${f.id}`;
        });
    }
    return this.reqService.get(url);
  }
}
